import { useEffect, useMemo, useState } from "react";

import { Box, CircularProgress, Typography } from "@mui/material";

import { createColumnHelper } from "@tanstack/react-table";

import { v4 as uuidv4 } from "uuid";

import TableField from "components/Table";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";

import deviceColumns from "./deviceColumns";

const Devices = ({ timeRange }) => {
  const { auth, standalone, isAuthReady } = useAuthScope();

  const { sendBatch } = useDataSourceManager();

  const [requestUuid] = useState(uuidv4());

  const [deviceData, setDeviceData] = useState([]);

  const [loading, setLoading] = useState(true);

  const columnHelper = useMemo(() => createColumnHelper(), []);

  const fromTime = useMemo(() => {
    if (!timeRange?.weekStart) {
      return undefined;
    }

    const [year, month, day] = timeRange.weekStart.split("-").map(Number);

    if (!year || !month || !day) {
      return undefined;
    }

    const date = new Date(year, month - 1, day, 0, 0, 0, 0);

    if (Number.isNaN(date.getTime())) {
      return undefined;
    }

    return Math.floor(date.getTime() / 1000);
  }, [timeRange?.weekStart]);

  const toTime = useMemo(() => {
    if (!timeRange?.weekEnd) {
      return undefined;
    }

    const [year, month, day] = timeRange.weekEnd.split("-").map(Number);

    if (!year || !month || !day) {
      return undefined;
    }

    const date = new Date(year, month - 1, day + 1, 0, 0, 0, 0);

    if (Number.isNaN(date.getTime())) {
      return undefined;
    }

    return Math.floor(date.getTime() / 1000);
  }, [timeRange?.weekEnd]);

  const columns = useMemo(
    () => deviceColumns(columnHelper, fromTime, toTime),
    [columnHelper, fromTime, toTime],
  );

  useEffect(() => {
    if (!isAuthReady) {
      return;
    }

    const summaryReportId = timeRange?.summaryReportId;

    if (!summaryReportId) {
      console.warn(">>> [Devices] summaryReportId is empty");

      setDeviceData([]);

      setLoading(false);

      return;
    }

    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/devices.read"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/devices.read";

    const restfulPlaceholders = standalone
      ? {
          orgId: auth.realm?.id,

          siteId: auth.sites?.id,

          summaryReportId,
        }
      : {
          entId: auth.realm?.id,

          orgId: auth.orgs?.id,

          siteId: auth.sites?.id,

          summaryReportId,
        };

    const load = async () => {
      try {
        setLoading(true);

        const { getSendBatchData } = await sendBatch([
          {
            apiKey,

            requestUuid,

            restfulPlaceholders,

            params: {},
          },
        ]);

        const response = getSendBatchData({
          requestUuid,
        });

        const items =
          response?.data?.items ?? response?.result?.data?.items ?? [];

        const formattedData = items.map((item, index) => ({
          id: item.id ?? item.macAddress ?? `${item.deviceName}-${index}`,

          deviceType: item.deviceType ?? "-",

          deviceName: item.deviceName ?? "-",

          ipAddress: item.ipAddress ?? "-",

          macAddress: item.macAddress ?? "-",

          segments: Array.isArray(item.segments) ? item.segments : [],
        }));

        setDeviceData(formattedData);
      } catch (error) {
        console.error(">>> [Devices] load error:", error);

        setDeviceData([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [auth, isAuthReady, requestUuid, sendBatch, standalone, timeRange]);

  const tableInstance = useTableInstance({
    tableName: "summaryReportDevices",

    data: deviceData,

    columns,

    getRowId: (row) => row.id,

    enableSorting: true,

    enablePagination: true,

    enableGlobalFilter: false,

    enableColumnPinning: false,

    enableColumnOrder: false,

    enableRowSelection: false,
  });

  return (
    <Box
      sx={{
        width: "100%",

        px: "20px",

        pb: "30px",

        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          fontSize: "16px",

          fontWeight: 600,

          color: "#0B2E83",

          lineHeight: "24px",

          mb: "14px",
        }}
      >
        Device Connectivity
      </Typography>

      <TableField
        {...tableInstance}
        pendingChildren={
          loading ? (
            <Box
              sx={{
                display: "flex",

                justifyContent: "center",

                alignItems: "center",

                py: 3,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Typography
              sx={{
                color: "#AAAAAA",

                py: 3,

                textAlign: "center",
              }}
            >
              No data available
            </Typography>
          )
        }
      />
    </Box>
  );
};

export default Devices;
